import { useQuery } from '@apollo/client/react'
import { CountriesDocument, type CountriesQuery } from '@/graphql/generated/graphql'

export type Country = CountriesQuery['countries'][number]
export type Province = Country['provinces'][number]
export type Municipality = Province['municipalities'][number]

/**
 * Catálogo de países → provincias → municipios. Datos geográficos estáticos,
 * por eso `cache-first`. Lo consumen el paso de Dirección de los wizards
 * (selector en cascada `LocationFields`) y el `ReviewStep` (resolver IDs →
 * nombres con `findLocationNames`).
 *
 * Nota: el scalar `id` de GraphQL llega como `string`; el form guarda los IDs
 * como `number` (ver `addressSchema`), por eso los consumidores convierten con
 * `Number(...)`.
 */
export function useCountries() {
  const { data, loading, error, refetch } = useQuery(CountriesDocument, {
    fetchPolicy: 'cache-first',
  })

  return {
    countries: data?.countries ?? [],
    loading,
    error,
    refetch,
  }
}

/**
 * Resuelve los IDs numéricos de una dirección a sus nombres legibles usando el
 * árbol de países. Pensado para el `ReviewStep`.
 */
export function findLocationNames(
  countries: Country[],
  address: { countryId: number; provinceId: number; municipalityId: number },
) {
  const country = countries.find((c) => Number(c.id) === address.countryId)
  const province = country?.provinces.find((p) => Number(p.id) === address.provinceId)
  const municipality = province?.municipalities.find(
    (m) => Number(m.id) === address.municipalityId,
  )

  return {
    countryName: country?.name ?? '—',
    provinceName: province?.name ?? '—',
    municipalityName: municipality?.name ?? '—',
  }
}
